import { useEffect, useMemo, useState } from "react"
import { ChevronDown, ChevronRight, ChevronUp } from "lucide-react"
import { EmptyState } from "./EmptyState"
import { HScroll } from "./HScroll"
import en from "../i18n/en"

const getVal = (col, row) => (col.value ? col.value(row) : row[col.key])

const SkeletonRows = ({ columns, rows = 8 }) => (
  <tbody>
    {Array.from({ length: rows }).map((_, i) => (
      <tr key={i}>
        {columns.map((c) => (
          <td key={c.key}>
            <div className="ds-skel" style={{ width: c.align === "right" ? "64%" : "80%", marginLeft: c.align === "right" ? "auto" : 0 }} />
          </td>
        ))}
      </tr>
    ))}
  </tbody>
)

export const DataTable = ({
  columns,
  rows,
  isLoading,
  onRowClick,
  initialSort,
  empty,
  // A caller-provided `.key` (e.g. "group-10" vs "order-10") must win over
  // `.id` — two different rows (a group and an unrelated order) can share
  // the same numeric id, and keying both by that raw id makes React treat
  // them as the same element, corrupting the table on updates (e.g.
  // expanding one of them).
  rowKey = (r, i) => r.key ?? r.id ?? i,
  rowClassName,
  density = "comfortable",
  pageSizeOptions = [10, 20, 50],
  defaultPageSize = 20,
  hidePagination = false,
  stickyFirst = true,
  stickyLast = false,
  expandedKeys,
  onToggleExpand,
  renderExpanded,
  sortRows = true,
  onSortChange,
}) => {
  const [sort, setSort] = useState(initialSort || null)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(defaultPageSize)

  useEffect(() => {
    setPage(0)
  }, [rows, pageSize, sort])

  const sorted = useMemo(() => {
    if (!rows) return []
    // An expanded parent's child rows (`depth > 0`) are inserted right after
    // it in `rows`. Sorting/paginating the flat list independently would
    // scatter those children away from their parent — e.g. a child sorted
    // to a later page would look like it "disappeared" from the expanded
    // group. Group each parent with its trailing children before sorting,
    // and only ever sort/paginate by that group as a unit.
    const groups = []
    let current = null
    rows.forEach((r) => {
      if (r.depth > 0 && current) current.children.push(r)
      else {
        current = { row: r, children: [] }
        groups.push(current)
      }
    })
    if (sortRows && sort) {
      const col = columns.find((c) => c.key === sort.key)
      if (col) {
        const dir = sort.dir === "asc" ? 1 : -1
        groups.sort((a, b) => {
          const va = getVal(col, a.row)
          const vb = getVal(col, b.row)
          if (va == null) return 1
          if (vb == null) return -1
          if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir
          return String(va).localeCompare(String(vb)) * dir
        })
      }
    }
    return groups.flatMap((g) => [g.row, ...g.children])
  }, [rows, sort, columns, sortRows])

  const total = sorted.length
  const pages = Math.max(1, Math.ceil(total / pageSize))
  const pageSafe = Math.min(page, pages - 1)
  const sliced = hidePagination ? sorted : sorted.slice(pageSafe * pageSize, pageSafe * pageSize + pageSize)

  const toggleSort = (key) => {
    const next =
      sort?.key === key ? { key, dir: sort.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
    setSort(next)
    onSortChange?.(next)
  }

  const lastIndex = columns.length - 1

  return (
    <div className="ds-table-shell">
      <HScroll className="ds-table-scroller">
        <table className={`ds-table${density === "compact" ? " ds-table--compact" : ""}`}>
          <thead>
            <tr>
              {columns.map((c, i) => {
                const active = sort?.key === c.key
                return (
                  <th
                    key={c.key}
                    className={[
                      c.align === "right" ? "ds-th-right" : "",
                      c.sortable ? "ds-sortable" : "",
                      stickyFirst && i === 0 ? "ds-sticky-first" : "",
                      stickyLast && i === lastIndex ? "ds-sticky-last" : "",
                      c.className || "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    style={c.width ? { width: c.width } : undefined}
                    onClick={c.sortable ? () => toggleSort(c.key) : undefined}
                    aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}
                    title={c.tooltip}
                  >
                    <span className="pc-th-inner">
                      {c.header}
                      {c.sortable &&
                        active &&
                        (sort.dir === "asc" ? (
                          <ChevronUp className="pc-sort-ico" aria-hidden />
                        ) : (
                          <ChevronDown className="pc-sort-ico" aria-hidden />
                        ))}
                    </span>
                  </th>
                )
              })}
            </tr>
          </thead>
          {isLoading ? (
            <SkeletonRows columns={columns} />
          ) : sliced.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={columns.length}>
                  {empty || <EmptyState title="Nothing here yet." />}
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {sliced.map((row, i) => {
                const key = rowKey(row, i)
                const expanded = expandedKeys?.has?.(row.id) || expandedKeys?.has?.(key)
                const canExpand = row.expandable || row.type === "group"
                return (
                  <FragmentRow
                    key={key}
                    row={row}
                    i={i}
                    columns={columns}
                    lastIndex={lastIndex}
                    stickyFirst={stickyFirst}
                    stickyLast={stickyLast}
                    onRowClick={onRowClick}
                    rowClassName={rowClassName}
                    canExpand={canExpand}
                    expanded={expanded}
                    onToggleExpand={onToggleExpand}
                    renderExpanded={renderExpanded}
                  />
                )
              })}
            </tbody>
          )}
        </table>
      </HScroll>
      {!hidePagination && !isLoading && total > 0 && (
        <div className="ds-table-pager">
          <span>{en.common.results(total)}</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <label>
              {en.common.rowsPerPage}{" "}
              <select
                className="pc-control"
                style={{ width: "auto", padding: "4px 28px 4px 8px", display: "inline-block" }}
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                {pageSizeOptions.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="pc-btn pc-btn--secondary pc-btn--sm"
              disabled={pageSafe <= 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Prev
            </button>
            <span>{en.common.pageOf(pageSafe + 1, pages)}</span>
            <button
              type="button"
              className="pc-btn pc-btn--secondary pc-btn--sm"
              disabled={pageSafe >= pages - 1}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </span>
        </div>
      )}
    </div>
  )
}

const FragmentRow = ({
  row,
  i,
  columns,
  lastIndex,
  stickyFirst,
  stickyLast,
  onRowClick,
  rowClassName,
  canExpand,
  expanded,
  onToggleExpand,
  renderExpanded,
}) => (
  <>
    <tr
      className={[
        onRowClick || canExpand ? "ds-row-click" : "",
        row.type === "group" || row.isParent ? "ds-row-parent" : "",
        row.depth > 0 ? "ds-row-child" : "",
        rowClassName?.(row) || "",
      ]
        .filter(Boolean)
        .join(" ") || undefined}
      tabIndex={onRowClick ? 0 : undefined}
      onClick={() => {
        if (canExpand && onToggleExpand) onToggleExpand(row)
        else onRowClick?.(row)
      }}
      onKeyDown={
        onRowClick || canExpand
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                if (canExpand && onToggleExpand) onToggleExpand(row)
                else onRowClick?.(row)
              }
            }
          : undefined
      }
    >
      {columns.map((c, ci) => (
        <td
          key={c.key}
          className={[
            c.align === "right" ? "ds-td-right" : "",
            c.strong ? "pc-td-strong" : "",
            stickyFirst && ci === 0 ? "ds-sticky-first" : "",
            stickyLast && ci === lastIndex ? "ds-sticky-last" : "",
            row.depth > 0 && ci === 0 ? "ds-td-indent" : "",
            c.className || "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {ci === 0 && canExpand ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              {expanded ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}
              {c.render ? c.render(row) : row[c.key] ?? "—"}
            </span>
          ) : c.render ? (
            c.render(row)
          ) : (
            row[c.key] ?? "—"
          )}
        </td>
      ))}
    </tr>
    {expanded && renderExpanded && (
      <tr className="ds-row-child">
        <td colSpan={columns.length} style={{ background: "var(--bg-subtle)" }}>
          {renderExpanded(row, i)}
        </td>
      </tr>
    )}
  </>
)

export default DataTable
